import { ZoomInOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { useAppSelector } from '../../redux/hooks'
import { MAP_TYPE } from '../components/MapTypeButton/MapTypeButton'
import { useCheckClaims } from './useCheckClaims'

const handleLinkClick = (
  e:
    | React.MouseEvent<HTMLAnchorElement, MouseEvent>
    | React.MouseEvent<HTMLButtonElement, MouseEvent>
) => e.stopPropagation()

type MapImage = {
  [key in MAP_TYPE]: {
    src: string
    link: string
  }
}

const buttonProps = {
  ghost: true,
  onClick: handleLinkClick,
  target: '_blank' as const,
  className: 'flex-1',
}

export const usePropertyImagePreviewProps = (
  imageState: [MAP_TYPE, React.Dispatch<React.SetStateAction<MAP_TYPE>>],
  mapImage: MapImage,
  propertyDetailPath?: string
) => {
  const tester = useCheckClaims('ApiVyvojar')
  const { lat, long } = useAppSelector(
    state =>
      state.property.ruian.data?.definicniBod || {
        lat: undefined,
        long: undefined,
      }
  )
  const ikatastrLink = `https://ikatastr.cz/#kde=${lat},${long},19&info=${lat},${long}`
  const seznamLink = `https://mapy.com/fnc/v1/showmap?center=${long},${lat}&zoom=18&ovl=1&marker=true`

  return useMemo(() => {
    if (!tester)
      return {
        maskClassName: '!cursor-default',
        mask: (
          <div className="gap-2 flex flex-col justify-center">
            <Button icon={<ZoomInOutlined />} ghost>
              Zvětšit obrázek
            </Button>
            <div className="gap-2 flex flex-wrap justify-center">
              <Button
                ghost
                target="_blank"
                href={mapImage[imageState[0]].link}
                onClick={handleLinkClick}
              >
                {imageState[0] === MAP_TYPE.SW && mapImage[imageState[0]].src
                  ? 'Přejít na Street View'
                  : 'Přejít na Google mapy'}
              </Button>
              {lat !== undefined && long !== undefined && (
                <Button href={seznamLink} {...buttonProps}>
                  Přejít na Mapy.cz
                </Button>
              )}
            </div>
            {!!propertyDetailPath && (
              <Link
                to={propertyDetailPath}
                onClick={handleLinkClick}
                target="_blank"
                className="w-full text-center"
              >
                <Button ghost>Přejít na detail nemovitosti</Button>
              </Link>
            )}
          </div>
        ),
      }

    return {
      maskClassName: '!cursor-default',
      mask: (
        <div className="gap-2 flex flex-col justify-center min-w-[55%]">
          <Button icon={<ZoomInOutlined />} ghost>
            Zvětšit obrázek
          </Button>
          <div className="basis-full text-center pt-4">Přejít na:</div>

          <div className="gap-2 flex flex-wrap justify-center">
            {lat !== undefined && long !== undefined && (
              <Button href={seznamLink} {...buttonProps}>
                Mapy.cz
              </Button>
            )}

            {mapImage[MAP_TYPE.PANO].src && (
              <Button
                key={MAP_TYPE.PANO}
                href={mapImage[MAP_TYPE.PANO].link}
                {...buttonProps}
              >
                Panorama
              </Button>
            )}
          </div>

          <div className="gap-2 flex flex-wrap justify-center">
            <Button
              key={MAP_TYPE.AERIAL}
              href={mapImage[MAP_TYPE.AERIAL].link}
              {...buttonProps}
            >
              Google mapy
            </Button>

            {mapImage[MAP_TYPE.SW].src && (
              <Button
                key={MAP_TYPE.SW}
                href={mapImage[MAP_TYPE.SW].link}
                {...buttonProps}
              >
                Street View
              </Button>
            )}
          </div>

          {lat !== undefined && long !== undefined && (
            <div className="gap-2 flex flex-wrap justify-center">
              <Button href={ikatastrLink} {...buttonProps}>
                iKatastr
              </Button>
            </div>
          )}

          {!!propertyDetailPath && (
            <Link
              to={propertyDetailPath}
              onClick={handleLinkClick}
              target="_blank"
              className="w-full text-center"
            >
              <Button ghost>Přejít na detail nemovitosti</Button>
            </Link>
          )}
        </div>
      ),
    }
  }, [
    tester,
    mapImage,
    imageState,
    propertyDetailPath,
    lat,
    long,
    seznamLink,
    ikatastrLink,
  ])
}

export const useAuctionImagePreviewProps = (
  imageState: [MAP_TYPE, React.Dispatch<React.SetStateAction<MAP_TYPE>>],
  mapImage: MapImage,
  auctionUrl: string
) => {
  return useMemo(
    () => ({
      maskClassName: '!cursor-default',
      mask: (
        <div className="gap-2 flex flex-wrap justify-center">
          <Button ghost>Zvětšit obrázek</Button>
          {imageState[0] !== MAP_TYPE.SW && imageState[0] !== MAP_TYPE.PANO && (
            <Button
              ghost
              target="_blank"
              href={mapImage[imageState[0]].link}
              onClick={handleLinkClick}
            >
              Přejít na mapu
            </Button>
          )}
          <a
            href={auctionUrl}
            onClick={handleLinkClick}
            target="_blank"
            className="w-full text-center"
            rel="noreferrer"
          >
            <Button ghost>Přejít do dražby</Button>
          </a>
        </div>
      ),
    }),
    [imageState, mapImage, auctionUrl]
  )
}
